import RowSelectionCheckbox from "@/components/molecules/table/RowSelectionCheckbox";
import {
  ColumnDef,
  Row,
  RowSelectionState,
  Table,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const SELECT_COLUMN_ID = "select";

interface UseRowSelectionProps {
  enabled: boolean;
  data: unknown[];
}

interface UseRowSelectionReturn {
  rowSelection: RowSelectionState;
  setRowSelection: (
    updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState),
  ) => void;
  selectColumn: ColumnDef<any, unknown>;
  selectedIds: string[];
  selectedRows: unknown[];
  offPageCount: number;
  clearSelection: () => void;
}

//* Échap ne doit pas vider la sélection pendant une saisie ou dans une modale
const shouldIgnoreEscape = (target: EventTarget | null) => {
  if (document.querySelector(".modal.show, .offcanvas.show")) return true;
  if (!(target instanceof HTMLElement)) return false;
  return target.matches(
    "textarea, select, [contenteditable='true'], input:not([type='checkbox'])",
  );
};

export const useRowSelection = ({
  enabled,
  data,
}: UseRowSelectionProps): UseRowSelectionReturn => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  //* Ligne d'ancrage pour Maj+clic
  const anchorIdRef = useRef<string | null>(null);
  //* Garde les lignes cochées sur les pages précédentes
  const selectedRowsCache = useRef(new Map<string, unknown>());

  const clearSelection = useCallback(() => {
    anchorIdRef.current = null;
    setRowSelection({});
  }, []);

  const toggleRow = (row: Row<any>, table: Table<any>, shiftKey: boolean) => {
    const rows = table.getRowModel().rows;
    const value = !row.getIsSelected();
    const anchorIndex = rows.findIndex(({ id }) => id === anchorIdRef.current);
    const rowIndex = rows.findIndex(({ id }) => id === row.id);
    anchorIdRef.current = row.id;

    if (!shiftKey || anchorIndex === -1) {
      row.toggleSelected(value);
      return;
    }

    const [from, to] = [anchorIndex, rowIndex].sort((a, b) => a - b);
    table.setRowSelection((old) => {
      const next = { ...old };
      rows.slice(from, to + 1).forEach((rangeRow) => {
        if (!rangeRow.getCanSelect()) return;
        if (value) next[rangeRow.id] = true;
        else delete next[rangeRow.id];
      });
      return next;
    });
  };
  const toggleRowRef = useRef(toggleRow);
  toggleRowRef.current = toggleRow;

  const selectColumn = useMemo<ColumnDef<any, unknown>>(
    () => ({
      id: SELECT_COLUMN_ID,
      enableSorting: false,
      enableHiding: false,
      enablePinning: false,
      header: ({ table }) => (
        <RowSelectionCheckbox
          aria-label="Sélectionner toutes les lignes de la page"
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={table.getIsSomePageRowsSelected()}
          disabled={!table.getRowModel().rows.length}
          onToggle={() =>
            table.toggleAllPageRowsSelected(!table.getIsAllPageRowsSelected())
          }
        />
      ),
      cell: ({ row, table }) => (
        <RowSelectionCheckbox
          aria-label="Sélectionner la ligne"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onToggle={(shiftKey) => toggleRowRef.current(row, table, shiftKey)}
        />
      ),
    }),
    [],
  );

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  const selectedRows = useMemo(() => {
    const cache = selectedRowsCache.current;
    data.forEach((row: any) => {
      if (rowSelection[row.id]) cache.set(String(row.id), row);
    });
    Array.from(cache.keys()).forEach((id) => {
      if (!rowSelection[id]) cache.delete(id);
    });
    return selectedIds.map((id) => cache.get(id)).filter(Boolean);
  }, [data, rowSelection, selectedIds]);

  const offPageCount = useMemo(() => {
    const pageIds = new Set(data.map((row: any) => String(row.id)));
    return selectedIds.filter((id) => !pageIds.has(id)).length;
  }, [data, selectedIds]);

  useEffect(() => {
    if (!enabled || !selectedIds.length) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (shouldIgnoreEscape(e.target)) return;
      clearSelection();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [enabled, selectedIds.length, clearSelection]);

  return {
    rowSelection,
    setRowSelection,
    selectColumn,
    selectedIds,
    selectedRows,
    offPageCount,
    clearSelection,
  };
};
